<template>
	<div class="branch-rules">
		<div class="header">
			<div class="main-title">Run the first branch whose condition is met</div>
			<div class="secondary-title">Only one branch will run.</div>
		</div>
		<div class="branch-list">
			<BranchRule
				:item="rule"
				v-for="rule in currentRulesWithoutDefaultBranch"
				:key="rule.id"
				@updateRule="updateRule"
				:enableDelete="enableDelete"
				@deleteRule="deleteRule"
				:hasEditPermissions="hasEditPermissions"
				:isLoadingDelete="loadingDeleteRuleIds.includes(rule.id)"
			/>
		</div>
		<div class="add-branch-link" v-if="hasEditPermissions">
			<AppLoader class="ml-12" v-if="isLoadingAddBranch" />
			<div v-else class="inline-link-style highlighted" @click="addRule">[+] Add branch</div>
		</div>
		<div class="default-branch">
			<BranchRule
				v-if="defaultBranch"
				:item="defaultBranch"
				@updateRule="updateRule"
				:enableDelete="false"
				:hasEditPermissions="hasEditPermissions"
			/>
		</div>
	</div>
</template>

<script>
import isEmpty from 'lodash/isEmpty';
import BranchRule from './BranchRule';
import { mapGetters } from 'vuex';
import ErrorHandlingService from '@/utils/error-handling-service';
import { EdgeTypes } from '@/components/FlowTree/flow-tree-service.const';
import { useNodeOutgoingEdges } from '@/store/modules/selectedNode/getters';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
import { addBranchPath, deleteBranchPath, updateBranchPath } from '@/store/modules/nodes/actions';

export default {
	name: 'BranchRules',
	components: {
		BranchRule,
	},
	props: {
		hasEditPermissions: { type: Boolean },
	},
	computed: {
		...mapGettersShorthand({ useNodeOutgoingEdges }),
		...mapGetters('selectedNode', ['editNode']),
		outgoingDivergeEdges() {
			return this.useNodeOutgoingEdges.filter((edge) => edge.type === EdgeTypes.Diverge);
		},
		currentRules() {
			return this.outgoingDivergeEdges.map((edge, i) => ({
				else: i === this.outgoingDivergeEdges.length - 1,
				id: edge.id,
				name: edge.branch_path.name,
				rules: isEmpty(edge.branch_path.rules) ? undefined : edge.branch_path.rules,
			}));
		},
		currentRulesWithoutDefaultBranch() {
			return this.currentRules.filter((rule) => !rule.else);
		},
		defaultBranch() {
			return this.currentRules.find((rule) => rule.else);
		},
		enableDelete() {
			return this.currentRulesWithoutDefaultBranch.length > 1;
		},
		branchRootId() {
			return this.editNode.id;
		},
	},
	methods: {
		...mapActionsShorthand({ addBranchPath, deleteBranchPath, updateBranchPath }),
		updateRule({ id, key, value }) {
			this.updateBranchPath({ branchRootId: this.branchRootId, branchPathId: id, partialObjectToUpdate: { [key]: value } });
		},
		async deleteRule(payload) {
			const { id: branchPathId } = payload;
			if (branchPathId) {
				try {
					this.loadingDeleteRuleIds.push(branchPathId);
					await this.deleteBranchPath({ branchRootId: this.branchRootId, branchPathId });
				} catch (e) {
					this.$toast.error(ErrorHandlingService.extractErrorMessage(e) || 'Error while deleting branch, please try again later');
					throw e;
				} finally {
					this.loadingDeleteRuleIds = this.loadingDeleteRuleIds.filter((id) => id !== branchPathId);
				}
			}
		},
		async addRule() {
			try {
				this.isLoadingAddBranch = true;
				await this.addBranchPath({ branchRootId: this.branchRootId });
			} catch (e) {
				this.$toast.error(ErrorHandlingService.extractErrorMessage(e) || 'Error while adding branch, please try again later');
				throw e;
			} finally {
				this.isLoadingAddBranch = false;
			}
		},
	},
	data() {
		return {
			loadingDeleteRuleIds: [],
			isLoadingAddBranch: false,
		};
	},
};
</script>

<style scoped lang="scss">
.branch-rules {
	padding: 15px 10px;

	.header {
		margin: 0 0 20px 10px;

		.main-title {
			font-size: 16px;
			font-weight: bold;
		}
		.secondary-title {
			font-size: 12px;
		}
	}
	.add-branch-link {
		display: inline-block;
		margin: 10px 0 30px 0;
	}
}
</style>
